import { CSSProperties, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getInterestBackgroundImg, getInterestUIDByURLName, getWithOutSpecialChars } from '../../../../utils';
import { IInterest } from '../../../../services/api/interest/interest-model-definitions.ts';
import { IDailyLookBook } from '../../../../services/api/lookBook/lookbook-model-definitions.ts';
import {
  CATEGORY_DESKTOP_AD_MAP_DATA,
  CATEGORY_MOBILE_AD_MAP_DATA,
  CATEGORY_SMALL_TABLED_AD_MAP_DATA,
  CATEGORY_TABLED_AD_MAP_DATA,
  INTEREST_HEIGHT,
  MOBILE_INTEREST_HEIGHT,
} from './constants';
import { LATEST_TITLE } from '../../constants';
import { setUpAds } from '../../utils';
import { useResize } from '../../../../hooks';
import InterestCarousel from '../../../../components/InterestCarousel/InterestCarousel';
import Footer from '../../../../../../components/Footer/Footer';
import CategorySkeleton from './components/CategorySkeleton/CategorySkeleton';
import SingleCard from '../../components/SingleCard/SingleCard';
import { getSSRTranslation } from '../../../../../../i18n';
import { useFotoscapeNavigate } from '../../../../../../hooks/navigation';
import { LOOKBOOK_PARAMETERS_TO_COPY } from '../../../Lookbook/contants.ts';
import { CommonPageDataContext } from '../../../../ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { loadAdScripts } from '../../../../utils/ads.ts';
import { isDntEnabled } from '@utils/dnt.ts';
import { LOOKBOOK_ROUTE_PATH, ROUTER_ANIMATION_TYPE } from '../../../../router/constants.ts';
import { genFetchInterestQuery } from '@moment/net/classic-fotoscapes/fetch-interests-page-data.ts';

import './index.scss';

// This component is now self-sufficient and no longer needs props.
const Category: FC = () => {
  const navigate = useFotoscapeNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useResize();

  const { interests, adData } = useContext(CommonPageDataContext);

  const { i18n } = useTranslation();
  const lang = i18n.language;

  // 1. Generate the list of queries to run based on the route and context.
  // This logic is shared with the server-side pre-fetcher.
  const queriesToRun = useMemo(() => {
    if (!params.interestTitle || !interests) {
      return [];
    }
    return genFetchInterestQuery({
      interestTitle: params.interestTitle,
      interests,
      lang,
    }).map((item) => {
      return {
        ...item,
        queryFn: item.queryFn(),
        queryKey: [...item.queryKey, lang]
      }
    });
  }, [params.interestTitle, interests, lang]);

  // 2. Execute all queries in parallel.
  const queryResults = useQueries({
    queries: queriesToRun,
  });

  // 3. Transform query results into a list of sections, each with its own data and loading state.
  // This is the core of the new robust experience.
  const sectionsData = useMemo(() => {
    return queriesToRun.map((queryDef, index) => {
      const result = queryResults[index];
      const interestData =
        interests.interestForId(queryDef.interestUid) || {
          uid: queryDef.interestUid,
          name: { en: 'Latest' },
          description: {
            en: 'Check out the most recent images and stories from the Fotoscapes team',
          },
        };

      return {
        interest: interestData as IInterest,
        lookbooks: result.data as IDailyLookBook[] | undefined,
        isLoading: result.isLoading,
      };
    });
  }, [queriesToRun, queryResults, interests, params.interestTitle]);

  // The primary interest for the page, used for ad targeting.
  // It's available immediately, even while data is loading.
  const interest = useMemo(() => {
    if (sectionsData.length > 0) {
      return sectionsData[0].interest;
    }
    return null;
  }, [sectionsData]);

  // --- The rest of the component logic remains largely the same ---
  const [, setRenderIndex] = useState(0);

  const sectionHeight = useMemo(() => {
    return isMobile ? MOBILE_INTEREST_HEIGHT : INTEREST_HEIGHT;
  }, [isMobile]);

  const interestPageHeight = useMemo(() => {
    const scrollTop = searchParams.get('scrollTop');
    const cardsInView = window.innerHeight / sectionHeight;
    return scrollTop ? `${Number(scrollTop) + sectionHeight * cardsInView}px` : '100vh';
  }, [searchParams, sectionHeight]);

  const interestStyleList = useMemo(() => {
    return { minHeight: interestPageHeight };
  }, [interestPageHeight]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const scrollTop = searchParams.get('scrollTop');
    if (scrollTop && sectionsData.some(s => !s.isLoading)) {
      window.scroll({ top: Number(scrollTop) });
    }
  }, [sectionsData, navigate, searchParams, setSearchParams]);

  const lookBookItemClassList = (uid: string) => {
    const classList = ['category__interest-list-item'];
    if (uid) {
      classList.push(`interest-background  interest-background--with-img interest-background--${uid}`);
    }
    return classList.join(' ');
  };

  const renderCarouselItem = ({ images, summary, title, brandLogo, uid, interests }: IDailyLookBook) => (
    <SingleCard
      uid={uid}
      interestId={interests[0]}
      link={{
        pathname: `${LOOKBOOK_ROUTE_PATH.replace(':lookbookId', uid).replace(':title?', getWithOutSpecialChars(title.en))}`,
        hash: '#0',
      }}
      state={{ animation: { out: ROUTER_ANIMATION_TYPE.routerAnimationFadeOut, in: ROUTER_ANIMATION_TYPE.routerAnimationFadeIn } }}
      classList="interest-carousel__cover-look"
      title={getSSRTranslation(title, i18n)}
      imgList={images}
      descriptionLineCount={4}
      description={getSSRTranslation(summary, i18n)}
      logoURL={brandLogo}
    />
  );

  // 4. This new function decides whether to render a skeleton or the actual content for a section.
  const renderSectionOrSkeleton = (section: typeof sectionsData[0]) => {
    const { interest, lookbooks, isLoading } = section;
    const { uid, name, description } = interest;

    // If this specific section is loading, show its skeleton.
    if (isLoading) {
      return <CategorySkeleton key={`skeleton-${uid}`} />;
    }

    // If it's done loading but has no items, render nothing for this section.
    if (!lookbooks || lookbooks.length === 0) {
      return null;
    }

    // Otherwise, render the full section with its data.
    const backgroundImage = getInterestBackgroundImg(interest);
    return (
      <div
        className="category__interest-list"
        id={uid}
        key={uid}
        style={{ '--background-image': `url(${backgroundImage})`, height: sectionHeight } as CSSProperties}
      >
        <section className={lookBookItemClassList(uid)}>
          <div className="interest-background__item" />
          <h1 className="category__title f-headline--xxxl">{getSSRTranslation(name, i18n)}</h1>
          {description && <h2 className="category__subtitle paragraph--m">{getSSRTranslation(description, i18n)}</h2>}
          <div className="category__lookbook-list">
            <InterestCarousel isMobile={isMobile} itemList={lookbooks} renderItem={renderCarouselItem} />
          </div>
        </section>
      </div>
    );
  };

  const renderAd = useCallback(() => {
    const parameterList = [];
    const size = [
      ...CATEGORY_DESKTOP_AD_MAP_DATA.adSizeList,
      ...CATEGORY_TABLED_AD_MAP_DATA.adSizeList,
      ...CATEGORY_SMALL_TABLED_AD_MAP_DATA.adSizeList,
      ...CATEGORY_MOBILE_AD_MAP_DATA.adSizeList,
    ];
    const responsiveSize = [
      CATEGORY_DESKTOP_AD_MAP_DATA,
      CATEGORY_TABLED_AD_MAP_DATA,
      CATEGORY_SMALL_TABLED_AD_MAP_DATA,
      CATEGORY_MOBILE_AD_MAP_DATA,
    ];

    for (const parameter of LOOKBOOK_PARAMETERS_TO_COPY) {
      const parameterValue = searchParams.get(parameter);
      if (parameterValue) {
        parameterList.push({ parameter, parameterValue });
      }
    }

    if (interest) {
      parameterList.push({ parameter: 'pathname', parameterValue: interest.name.en });
    }

    if (adData) {
      setUpAds(adData.adTag, 'category', size, parameterList, responsiveSize);
    }
  }, [searchParams, adData, interest]);

  // Ad logic is now simpler and doesn't depend on a global loading state.
  useEffect(() => {
    if (adData) {
      renderAd();
      if (!isDntEnabled()) loadAdScripts(adData.adTag);
    }
  }, [renderAd, adData]);

  useEffect(() => {
    setRenderIndex(0);
  }, [params]);

  // 5. The main render block no longer has a global `isLoading` check.
  // It immediately renders the page structure and maps over the sections.
  return (
    <div className="category" style={interestStyleList}>
      <div>
        {sectionsData.slice(0, 1).map(renderSectionOrSkeleton)}
        <div key="ad" className="category__ad" id="ad_unit_category"></div>
        {sectionsData.slice(1).map(renderSectionOrSkeleton)}
      </div>
      <Footer />
    </div>
  );
};

export default Category;
